import React, { useEffect, useState } from 'react';
import { Button, Col, Drawer, Form, Input, Row, Table, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useLazyQuery } from '@apollo/client';
import TablePaginationBar from 'components/TablePaginationBar';
import {
    LOADING_ARCHIVE_DETAILS,
    LoadingArchiveDetailFilter,
    LoadingArchiveDetailsResponse,
    LoadingOrderDetailItem,
} from '../graphql/queries/loadingArchiveQueries';
import { DEFAULT_PAGINATION, renderText, renderTime } from '../../task-management/components/taskManagementShared';
import { ARCHIVE_PAGE_SIZE_OPTIONS, renderDataSource, renderEventStatus } from './loadingArchiveShared';

type Props = {
    task: { id: number; taskNumber: string | null } | null;
    onClose: () => void;
};

type FilterValues = { trackingNumber?: string; bagNumber?: string };

const columns: ColumnsType<LoadingOrderDetailItem> = [
    { title: 'ID', dataIndex: 'id', width: 100, render: renderText },
    { title: '运单号', dataIndex: 'trackingNumber', width: 170, render: renderText },
    { title: '包牌号', dataIndex: 'bagNumber', width: 150, render: renderText },
    { title: '包牌ID', dataIndex: 'bagId', width: 100, render: renderText },
    { title: '订单ID', dataIndex: 'orderId', width: 110, render: renderText },
    { title: '装车明细ID', dataIndex: 'tmsLoadingDetailId', width: 110, render: renderText },
    { title: '装车途经点ID', dataIndex: 'waypointId', width: 120, render: renderText },
    { title: '装车时间', dataIndex: 'loadingTime', width: 170, render: renderTime },
    { title: '目的站点ID', dataIndex: 'destinationWarehouseId', width: 110, render: renderText },
    { title: '分拣代码', dataIndex: 'orderSortingCode', width: 110, render: renderText },
    { title: '装车扫描事件', dataIndex: 'loadingScanEventStatus', width: 120, render: renderEventStatus },
    { title: '尾程交接事件', dataIndex: 'handoverEventStatus', width: 120, render: renderEventStatus },
    { title: '卸车途经点ID', dataIndex: 'unloadingTaskWaypointId', width: 120, render: renderText },
    { title: '卸车时间', dataIndex: 'unloadingTime', width: 170, render: renderTime },
    {
        title: '已移除',
        dataIndex: 'isDeleted',
        width: 80,
        render: (v: number | null) => (v === 1 ? <Tag color="red">是</Tag> : <Tag>否</Tag>),
    },
    { title: '移除时间', dataIndex: 'removeTime', width: 170, render: renderTime },
];

/** 「查看装车包牌」抽屉：后端按任务归档情况自动选表（未归档读在线表、已归档读归档表） */
const LoadingOrderDetailDrawer: React.FC<Props> = ({ task, onClose }) => {
    const [form] = Form.useForm<FilterValues>();
    const [activeFilter, setActiveFilter] = useState<LoadingArchiveDetailFilter>({});
    const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
    const [runQuery, { data, loading }] = useLazyQuery<LoadingArchiveDetailsResponse>(LOADING_ARCHIVE_DETAILS, {
        fetchPolicy: 'network-only',
        onError: (e) => message.error(e.message),
    });

    // 切换任务（含关闭后再次打开）时重置筛选与分页
    useEffect(() => {
        form.resetFields();
        setActiveFilter({});
        setPagination(DEFAULT_PAGINATION);
    }, [task?.id, form]);

    useEffect(() => {
        if (!task) return;
        runQuery({
            variables: {
                taskId: task.id,
                filter: activeFilter,
                pageRequest: { page: pagination.current, size: pagination.pageSize },
            },
        });
    }, [task, activeFilter, pagination, runQuery]);

    const handleSearch = () => {
        const values = form.getFieldsValue();
        setPagination((prev) => ({ ...prev, current: 1 }));
        setActiveFilter({
            trackingNumber: values.trackingNumber?.trim() || undefined,
            bagNumber: values.bagNumber?.trim() || undefined,
        });
    };

    const result = data?.opsToolTmsLoadingArchiveDetails;

    return (
        <Drawer
            open={!!task}
            width={1100}
            destroyOnClose
            onClose={onClose}
            title={
                <span>
                    装车包牌{task ? ` · 任务 ${task.taskNumber ?? task.id}` : ''}
                    <span className="ml-3 align-middle">{renderDataSource(result?.dataSource)}</span>
                </span>
            }
        >
            <Form form={form}>
                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item name="trackingNumber" label="运单号">
                            <Input allowClear placeholder="精确匹配" />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="bagNumber" label="包牌号">
                            <Input allowClear placeholder="精确匹配" />
                        </Form.Item>
                    </Col>
                    <Col span={8} className="text-right">
                        <Form.Item>
                            <Button className="mr-2" onClick={() => form.resetFields()}>
                                重置
                            </Button>
                            <Button type="primary" loading={loading} onClick={handleSearch}>
                                查询
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
            <Table<LoadingOrderDetailItem>
                rowKey="id"
                size="small"
                loading={loading}
                columns={columns}
                dataSource={result?.content ?? []}
                pagination={false}
                scroll={{ x: 1900 }}
                className="mb-4"
            />
            <TablePaginationBar
                total={result?.totalElements ?? 0}
                current={pagination.current}
                pageSize={pagination.pageSize}
                pageSizeOptions={ARCHIVE_PAGE_SIZE_OPTIONS}
                onChange={(page, size) => setPagination({ current: page, pageSize: size })}
            />
        </Drawer>
    );
};

export default LoadingOrderDetailDrawer;
